/**
 * BYOP 认证面板组件
 * 支持两种模式：
 *   - 生产模式 (isEmbedded=true): Key 通过环境变量预配置，页面只显示登录按钮
 *   - 开发模式 (isEmbedded=false): 显示 Key 输入框，方便本地调试
 */

import React, { useEffect } from 'react'
import { useBYOPAuth } from '../hooks/usePollinations'

export default function AuthPanel({ appKey, onKeyChange, isEmbedded = false }) {
  const { userKey, userInfo, loading, initiateAuth, handleCallback, logout } = useBYOPAuth(appKey)

  // 处理 OAuth 回调
  useEffect(() => {
    handleCallback()
  }, [handleCallback])

  const isLoggedIn = !!userKey

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🔐 用户授权</h3>
        <p style={styles.desc}>
          使用 Bring Your Own Pollen 模式，授权本应用使用您的 Pollen 额度。
          {!isEmbedded && (
            <>
              <br />
              <span style={styles.small}>开发者从您的使用中获得 25% 收益分成</span>
            </>
          )}
        </p>
      </div>

      {!isLoggedIn ? (
        <div style={styles.form}>
          {/* 开发模式：显示 Key 输入框 */}
          {!isEmbedded && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>App Key (pk_...)</label>
                <input
                  type="text"
                  value={appKey}
                  onChange={(e) => onKeyChange(e.target.value)}
                  placeholder="pk_your_publishable_key"
                  style={styles.input}
                />
                <span style={styles.hint}>开发者从 enter.pollinations.ai 获取</span>
              </div>

              <div style={styles.orDivider}>
                <span style={styles.orText}>或</span>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>开发者 Secret Key (sk_...)</label>
                <input
                  type="password"
                  value={localStorage.getItem('pollinations_dev_key') || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      localStorage.setItem('pollinations_dev_key', e.target.value)
                    } else {
                      localStorage.removeItem('pollinations_dev_key')
                    }
                  }}
                  placeholder="sk_your_secret_key"
                  style={styles.input}
                />
                <span style={styles.hint}>仅本地使用，不传输到服务器</span>
              </div>
            </>
          )}

          {/* 生产模式：轻量提示 */}
          {isEmbedded && (
            <div style={styles.embeddedNotice}>
              <span style={styles.embeddedDot}></span>
              应用已配置，点击下方按钮授权登录
            </div>
          )}

          <button
            onClick={initiateAuth}
            disabled={loading || !appKey}
            style={{
              ...styles.button,
              ...(loading || !appKey ? styles.buttonDisabled : {}),
            }}
          >
            {loading ? '跳转中...' : '使用 Pollinations 登录授权'}
          </button>
        </div>
      ) : (
        <div style={styles.loggedIn}>
          <div style={styles.userInfo}>
            {userInfo?.picture && (
              <img src={userInfo.picture} alt="avatar" style={styles.avatar} />
            )}
            <div>
              <div style={styles.userName}>
                {userInfo?.preferred_username || userInfo?.name || '已授权用户'}
              </div>
              <div style={styles.userEmail}>{userInfo?.email}</div>
            </div>
          </div>
          <button onClick={logout} style={styles.logoutButton}>
            退出登录
          </button>
        </div>
      )}

      <div style={styles.footer}>
        <a
          href="https://github.com/pollinations/pollinations/blob/main/BRING_YOUR_OWN_POLLEN.md"
          target="_blank"
          rel="noopener noreferrer"
          style={styles.link}
        >
          什么是 BYOP？
        </a>
      </div>
    </div>
  )
}

const styles = {
  container: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(138, 43, 226, 0.3)',
    marginBottom: '20px',
  },
  header: {
    marginBottom: '20px',
  },
  title: {
    margin: '0 0 8px 0',
    color: '#e2e8f0',
    fontSize: '18px',
  },
  desc: {
    margin: 0,
    color: '#94a3b8',
    fontSize: '13px',
    lineHeight: 1.6,
  },
  small: {
    fontSize: '12px',
    color: '#8a2be2',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#cbd5e1',
    fontSize: '13px',
    fontWeight: 500,
  },
  input: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    background: 'rgba(15, 23, 42, 0.6)',
    color: '#e2e8f0',
    fontSize: '14px',
    outline: 'none',
  },
  hint: {
    fontSize: '11px',
    color: '#64748b',
  },
  orDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#64748b',
    fontSize: '12px',
  },
  orText: {
    flexShrink: 0,
  },
  embeddedNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: 'rgba(34, 197, 94, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    color: '#94a3b8',
    fontSize: '13px',
  },
  embeddedDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#22c55e',
    flexShrink: 0,
  },
  button: {
    padding: '12px 20px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #8a2be2, #6b21a8)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  loggedIn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    background: 'rgba(34, 197, 94, 0.1)',
    borderRadius: '12px',
    border: '1px solid rgba(34, 197, 94, 0.3)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
  },
  userName: {
    color: '#e2e8f0',
    fontWeight: 600,
    fontSize: '14px',
  },
  userEmail: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  logoutButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(239, 68, 68, 0.4)',
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    fontSize: '13px',
    cursor: 'pointer',
  },
  footer: {
    marginTop: '16px',
    textAlign: 'center',
  },
  link: {
    color: '#8a2be2',
    fontSize: '12px',
    textDecoration: 'none',
  },
}